import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import { Redirect } from 'react-router';
import { useApolloClient, useQuery, gql } from '@apollo/client';

import { FullPageSpinner } from 'admin-components/components';
import AgentPortalError from 'errors/AgentPortalError';
import { GET_SEARCH, GET_BOOKINGS, GET_BOOKINGS_PER_TRAIN } from 'store/queries';
import { ACTION_CODES } from 'constants/errors';
import { historyShape } from 'constants/propTypes';

import BookingSearchTable from './BookingSearchTable';
import BookingSearchSummary from './BookingSearchSummary';
import { useBookingForStandardSearchTable } from './utils';

const BookingStandardSearchResults = ({
  history: {
    location: { search }
  }
}) => {
  const { reference, lastName, departureDate, travelEndDate, email, serviceName, trainNumber, externalReference, coach, seat } =
    querystring.parse(search.substr(1));

  const [includeCancelled, setIncludeCancelled] = useState(false);

  const searchParams = useMemo(
    () =>
      reference
        ? {
            reference
          }
        : trainNumber
        ? {
            departureDate,
            trainNumber,
            reference: externalReference,
            includeCancelled,
            ...(coach && { coach: Number(coach).toString() }),
            ...(seat && { seat: Number(seat).toString() })
          }
        : {
            departureDate,
            travelEndDate,
            passengerName: lastName,
            passengerEmail: email,
            ...(serviceName && { serviceName })
          },
    [reference, lastName, departureDate, travelEndDate, email, serviceName, trainNumber, externalReference, coach, seat, includeCancelled]
  );

  const client = useApolloClient();

  const query = reference ? GET_BOOKINGS : trainNumber ? GET_BOOKINGS_PER_TRAIN : GET_SEARCH;
  const { data, error, loading } = useQuery(query, {
    variables: searchParams,
    fetchPolicy: 'no-cache'
  });

  const bookings = (reference ? data?.bookings : trainNumber ? data?.bookingsPerTrain : data?.bookingSearch) ?? [];

  const bookingsData = useBookingForStandardSearchTable(bookings, !!trainNumber && (!!coach || !!seat || !externalReference));

  if (error) {
    client.writeQuery({
      query: gql`
        query {
          searchParams @client {
            reference
            departureDate
            travelEndDate
            passengerName
            passengerEmail
            serviceName
            trainNumber
            externalReference
            coach
            seat
            includeCancelled
          }
        }
      `,
      data: {
        searchParams: {
          ...searchParams,
          __typename: 'SearchParams'
        }
      }
    });
  }

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/search/booking'
    });
  }

  let shouldRedirectToDetails = false;
  const shouldUseColumnsStandardColumns = !coach && !seat && externalReference;

  if (bookings.length > 1) {
    const bRef = bookings[0].reference;

    shouldRedirectToDetails = bookings.every(
      (b) => b.reference === bRef && b.externalBookingReferences.find((ebr) => ebr.externalId === bRef)
    );
  }

  if (bookings.length === 1) {
    shouldRedirectToDetails = true;
  }

  if (shouldRedirectToDetails) {
    return <Redirect to={`/manage/bookings/${bookings[0].reference}`} />;
  }

  return (
    <>
      <BookingSearchSummary
        reference={reference}
        noOfPnrsFound={bookings.length}
        departureDate={departureDate}
        travelEndDate={travelEndDate}
        passengerName={lastName}
        passengerEmail={email}
        serviceName={serviceName}
        trainNumber={trainNumber}
        coach={coach}
        seat={seat}
        shouldShowBookingFilter={!reference && !!trainNumber}
        includeCancelled={includeCancelled}
        setIncludeCancelled={setIncludeCancelled}
        data-testid="booking-search-summary"
      />
      {loading && <FullPageSpinner data-testid="booking-standard-search-results-loading" />}
      {bookings.length > 0 && (
        <BookingSearchTable
          bookings={bookingsData}
          columnsCoachAndSeat={Boolean(coach) || Boolean(seat)}
          columnsTicket={!shouldUseColumnsStandardColumns}
          data-testid="booking-search-table"
          removeColumns={['minorsAmount', 'specialAssistance']}
        />
      )}
    </>
  );
};

BookingStandardSearchResults.propTypes = {
  history: historyShape,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func
  })
};

export default BookingStandardSearchResults;
